import { inject, Injectable } from '@angular/core';
import { PopupNotificationsService } from '@principle-theorem/ng-interactions';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  IPopupNotification,
  PopupNotification,
} from '@principle-theorem/notifications';
import { Recalls } from '@principle-theorem/principle-core';
import {
  BrandRecallField,
  IBrand,
  IPatient,
  IRecallSettings,
  IStaffer,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  bufferedQuery$,
  DocumentReference,
  FieldValue,
  Firestore,
  multiMergeMap,
  Query,
  snapshotDefined,
  WithRef,
} from '@principle-theorem/shared';
import { scan, tap } from 'rxjs/operators';
import {
  BrandRecallSettingsDialogComponent,
  IBrandRecallSettingsDialogRequest,
  IBrandRecallSettingsDialogResponse,
  IBrandRecallUpdateFormData,
} from './brand-recall-settings-dialog/brand-recall-settings-dialog.component';
import {
  IPatientRecallSettingsDialogRequest,
  IPatientRecallSettingsDialogResponse,
  PatientRecallSettingsDialogComponent,
} from './patient-recall-settings-dialog/patient-recall-settings-dialog.component';

@Injectable()
export class RecallsService {
  private _dialog = inject(BasicDialogService);
  private _popupNotifications = inject(PopupNotificationsService);
  private _organisation = inject(OrganisationService);

  async editBrandRecallSettings(
    brand: WithRef<IBrand>,
    track: RecallTrack
  ): Promise<void> {
    const response = await this._dialog
      .open<
        BrandRecallSettingsDialogComponent,
        IBrandRecallSettingsDialogRequest,
        IBrandRecallSettingsDialogResponse
      >(
        BrandRecallSettingsDialogComponent,
        DialogPresets.medium({ data: { brand, track } })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    if (response.action === 'disable') {
      return this.disableSecondaryRecall(brand);
    }

    const wasSecondaryEnabled = !!brand.settings.secondaryRecalls;
    const settingsField = trackToSettingsField(track);
    await Firestore.saveDoc({
      ...brand,
      settings: { ...brand.settings, [settingsField]: response.settings },
    });

    if (!response.settings) {
      return;
    }

    const activatingSecondary =
      track === RecallTrack.Secondary && !wasSecondaryEnabled;

    await this._updatePatientRecalls(
      brand.ref,
      response.settings,
      response.updateForm,
      track,
      activatingSecondary
    );
  }

  async enableSecondaryRecall(brand: WithRef<IBrand>): Promise<void> {
    return this.editBrandRecallSettings(brand, RecallTrack.Secondary);
  }

  async disableSecondaryRecall(brand: WithRef<IBrand>): Promise<void> {
    const label = Recalls.trackLabel(brand, RecallTrack.Secondary);
    const confirmed = await this._dialog.confirm({
      title: `Disable ${label}`,
      prompt: `This will clear the next ${label.toLowerCase()} date for every patient that has one (manually-set dates and individually-disabled patients are preserved) and remove the brand's secondary recall settings. You can re-enable later.`,
      submitLabel: `Disable ${label}`,
      cancelLabel: 'Cancel',
    });
    if (!confirmed) {
      return;
    }

    const notification = new PopupNotificationManager(
      this._popupNotifications,
      await snapshotDefined(this._organisation.staffer$)
    );
    notification.open(
      PopupNotification.init({
        title: `Disabling ${label}`,
        content: 'Clearing patient recall dates...',
        hasTimeout: false,
      })
    );

    await this._processBufferedQuery(
      Recalls.patientsWithAutoRecallDueDateQuery(
        brand.ref,
        RecallTrack.Secondary
      ),
      notification,
      (patient) => this._clearAutoRecallDueDate(patient, RecallTrack.Secondary),
      (processed, total) =>
        `Clearing ${label.toLowerCase()} for ${processed}/${total} patients`
    );

    // Patient writes complete first, brand setting cleared last - reversing
    // this would leave orphaned patient state if the user closes the tab.
    await Firestore.updateDoc(brand.ref, {
      'settings.secondaryRecalls': FieldValue.delete(),
    } as unknown as Partial<IBrand>);

    await notification.update('Done', true);
  }

  async editPatientRecallSettings(
    patient: WithRef<IPatient>,
    track: RecallTrack
  ): Promise<void> {
    const response = await this._dialog
      .open<
        PatientRecallSettingsDialogComponent,
        IPatientRecallSettingsDialogRequest,
        IPatientRecallSettingsDialogResponse
      >(
        PatientRecallSettingsDialogComponent,
        DialogPresets.medium({ data: { patient, track } })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    await Firestore.patchDoc(patient.ref, {
      [Recalls.trackToField(response.track)]: response.recall,
    });
  }

  private async _clearAutoRecallDueDate(
    patient: WithRef<IPatient>,
    track: RecallTrack
  ): Promise<void> {
    const field = Recalls.trackToField(track);
    const existing = patient[field];
    if (!existing) {
      return;
    }
    await Firestore.patchDoc(patient.ref, {
      [field]: {
        ...existing,
        nextRecallDue: {
          isSet: false,
          isExplicit: false,
          dueAt: undefined,
        },
      },
    });
  }

  private async _updatePatientRecalls(
    brandRef: DocumentReference<IBrand>,
    settings: IRecallSettings,
    updateForm: IBrandRecallUpdateFormData,
    track: RecallTrack = RecallTrack.Primary,
    activateAllForTrack: boolean = false
  ): Promise<void> {
    const notification = new PopupNotificationManager(
      this._popupNotifications,
      await snapshotDefined(this._organisation.staffer$)
    );
    notification.open(
      PopupNotification.init({
        title: `Updating patient recalls`,
        content: 'Finding patients...',
        hasTimeout: false,
      })
    );

    if (activateAllForTrack) {
      const brand = await Firestore.getDoc(brandRef);
      const trackLabel = Recalls.trackLabel(brand, track).toLowerCase();
      await this._processBufferedQuery(
        Recalls.enabledPatientsWithoutRecallDueDateQuery(brandRef, track),
        notification,
        (patient) => this._updateNextRecallDue(patient, settings, track),
        (processed, total) =>
          `Scheduling ${trackLabel} for ${processed}/${total} patients`
      );
    }

    await this._processBufferedQuery(
      Recalls.patientsWithOutdatedDefaultPeriodQuery(brandRef, settings, track),
      notification,
      (patient) => this._updateRecallPeriod(patient, settings, track),
      (processed, total) =>
        `Updating ${processed}/${total} patients recall periods`
    );

    if (updateForm.addMissing) {
      await this._processBufferedQuery(
        Recalls.patientsWithoutRecallDueDateQuery(brandRef, track),
        notification,
        (patient) => this._updateNextRecallDue(patient, settings, track),
        (processed, total) =>
          `Adding next recall due for ${processed}/${total} patients without one`
      );
    }

    if (updateForm.updateExisting) {
      await this._processBufferedQuery(
        Recalls.patientsWithAutoRecallDueDateQuery(brandRef, track),
        notification,
        (patient) => this._updateNextRecallDue(patient, settings, track),
        (processed, total) =>
          `Updating next recall due for ${processed}/${total} patients that had them set automatically`
      );
    }

    if (updateForm.overrideExisting) {
      await this._processBufferedQuery(
        Recalls.patientsWithExplicitRecallDueDateQuery(brandRef, track),
        notification,
        (patient) => this._updateNextRecallDue(patient, settings, track),
        (processed, total) =>
          `Overriding next recall due for ${processed}/${total} patients that had them set manually`
      );
    }

    await notification.update('Done', true);
  }

  private async _updateRecallPeriod(
    patient: WithRef<IPatient>,
    settings: IRecallSettings,
    track: RecallTrack
  ): Promise<void> {
    const field = Recalls.trackToField(track);
    const existing = patient[field];
    if (existing?.periodInMonths === settings.periodInMonths) {
      return;
    }
    if (!existing) {
      return;
    }
    await Firestore.patchDoc(patient.ref, {
      [field]: {
        ...existing,
        periodInMonths: settings.periodInMonths,
      },
    });
  }

  private async _updateNextRecallDue(
    patient: WithRef<IPatient>,
    settings: IRecallSettings,
    track: RecallTrack
  ): Promise<void> {
    const field = Recalls.trackToField(track);
    await Firestore.patchDoc(patient.ref, {
      [field]: await Recalls.buildPatientRecall(
        patient,
        settings,
        track,
        undefined,
        true,
        true
      ),
    });
  }

  private async _processBufferedQuery(
    patientQuery: Query<IPatient>,
    notification: PopupNotificationManager,
    actionCallback: (patient: WithRef<IPatient>) => Promise<void>,
    progressCallback: (processed: number, total: number) => string
  ): Promise<void> {
    const total = await Firestore.getCount(patientQuery);
    await bufferedQuery$(patientQuery, 100, 'ref')
      .pipe(
        multiMergeMap(25, (patient) => actionCallback(patient)),
        scan((acc) => acc + 1, 0),
        tap((processed) => {
          if (processed >= total || processed % 100 === 0) {
            void notification.update(progressCallback(processed, total), false);
          }
        })
      )
      .toPromise();
  }
}

function trackToSettingsField(track: RecallTrack): BrandRecallField {
  return track === RecallTrack.Primary ? 'recalls' : 'secondaryRecalls';
}

class PopupNotificationManager {
  private _notification?: IPopupNotification;

  constructor(
    private _popupNotifications: PopupNotificationsService,
    private _staffer: WithRef<IStaffer>
  ) {}

  open(notification: IPopupNotification): void {
    this._notification = notification;
    void this._popupNotifications.addNotification(
      this._notification,
      this._staffer.ref
    );
  }

  async update(content: string, hasTimeout: boolean = false): Promise<void> {
    if (!this._notification) {
      return;
    }
    return this._popupNotifications.addNotification(
      {
        ...this._notification,
        content,
        hasTimeout,
      },
      this._staffer.ref
    );
  }
}
